import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FooterActions,
  ModalCancelButton,
  InputLabel,
  TextInput,
  ConfirmText,
  ConfirmKeyword,
  ConfirmCloseButton,
  FormGroup,
} from './Confirmclosemodal.styles.js';

const CONFIRM_WORD = 'confirm';

const ConfirmCloseModal = ({ isOpen, onClose, onConfirm, isLoading = false, ticketTitle }) => {
  const [confirmText, setConfirmText] = useState('');
  const inputRef = useRef(null);

  const isMatch = confirmText.trim().toLowerCase() === CONFIRM_WORD;

  useEffect(() => {
    if (!isOpen) return;
    setConfirmText('');
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isMatch || isLoading) return;
    onConfirm?.();
  };

  return createPortal(
    <ModalOverlay role="presentation" onClick={handleClose}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-close-title"
        aria-describedby="confirm-close-description"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="confirm-close-title">Close ticket?</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={handleClose}
            aria-label="Close dialog"
            disabled={isLoading}
          >
            <X size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <ConfirmText id="confirm-close-description">
              {ticketTitle ? (
                <>
                  You are about to close <strong>{ticketTitle}</strong>.
                </>
              ) : (
                'You are about to close this ticket.'
              )}{' '}
              Once it is closed, you will no longer be able to send replies.
            </ConfirmText>

            <FormGroup>
              <InputLabel htmlFor="confirm-close-input">
                Type <ConfirmKeyword>{CONFIRM_WORD}</ConfirmKeyword> to close this ticket
              </InputLabel>
              <TextInput
                id="confirm-close-input"
                ref={inputRef}
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                autoComplete="off"
                spellCheck={false}
                disabled={isLoading}
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <FooterActions>
              <ModalCancelButton type="button" onClick={handleClose} disabled={isLoading}>
                Cancel
              </ModalCancelButton>
              <ConfirmCloseButton type="submit" disabled={!isMatch || isLoading}>
                {isLoading && <Loader2 style={{ animation: 'spin 1s linear infinite' }} />}
                {isLoading ? 'Closing…' : 'Close ticket'}
              </ConfirmCloseButton>
            </FooterActions>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default ConfirmCloseModal;
